import { API_URL, API_BASE, WS_URL } from '../config/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { useLanguage } from '../context/LanguageContext';

const API = API_URL;

const SearchPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [searchInput, setSearchInput] = useState(query);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchResults = useCallback(async () => {
    if (!query) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: query, page, limit: 20 }
      });
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / 20));
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    setSearchInput(query);
    setPage(1);
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="search-page">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">{t('commerce.home')}</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{t('commerce.search')}</span>
        </nav>

        {/* Search Form */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder={t('commerce.searchProducts')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pr-10"
                data-testid="search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button type="submit">
              <Search className="w-4 h-4 mr-2" /> {t('nav.search')}
            </Button>
          </form>
        </div>

        {/* Results Header */}
        {query && (
          <div className="mb-6">
            <h1 className="text-2xl font-bold">
              {t('commerce.searchResults')} "{query}"
            </h1>
            <p className="text-muted-foreground">
              {total} {total > 1 ? t('commerce.productsFoundPlural') : t('commerce.productsFound')}
            </p>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-square rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : !query ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('commerce.searchProductsTitle')}</h2>
            <p className="text-muted-foreground">
              Entrez un terme de recherche pour trouver des produits
            </p>
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Précédent
                </Button>
                <span className="px-4 text-sm text-muted-foreground">
                  Page {page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('commerce.noResults')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('commerce.noResultsDescription')} "{query}"
            </p>
            <Button asChild variant="outline">
              <Link to="/categories">{t('commerce.exploreCategories')}</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
